/**
 * The guest cart handle, on the transport a cookie cannot take.
 *
 * On the storefront the handle rides the ``dxf_cart_session`` cookie and
 * nothing here runs. Inside the embed it cannot: the iframe is
 * same-origin with our API (Vercel rewrites ``/api/*``), but the
 * *top-level* site is the tenant's customer's website, so the browser
 * treats the cookie as third-party and Safari's ITP and Firefox's ETP
 * drop it. The cart would forget itself for a large share of visitors,
 * silently.
 *
 * So in the embed the handle lives in ``localStorage`` on our own
 * origin and travels in the ``X-Guest-Session`` header instead. The
 * backend has accepted that transport since em01.
 *
 * **Minted client-side on purpose.** The server keeps no registry of
 * valid handles — a handle is a cart key, nothing more — so a
 * round-trip to mint one would add latency and no security.
 * ``crypto.getRandomValues`` supplies the same entropy as the server's
 * ``secrets.token_urlsafe(24)``.
 *
 * See ``docs/specs/spec_embed_widget.md`` §E0/§E4.
 */

const STORAGE_KEY = "forgmatic.guest-session";

import { markEmbedHandover } from "@/embed/surface";
import { apiFetch } from "@/lib/api";

/**
 * Key carrying the cart across the breakout to the shop.
 *
 * Lives in the URL **fragment**, not the query string (KAN-506): the
 * breakout is a document navigation, so a query would ride the request
 * line into every access log between the browser and us. A fragment
 * never leaves the browser.
 *
 * What it carries is a one-time **handoff token**, never the handle
 * itself (KAN-1258): the handle is the cart's capability, and a link
 * that installs it in whoever opens it is a session-fixation attack —
 * the visitor shops in the attacker's cart and the attacker reads the
 * order. The token is minted by ``POST /guest-session/handoff``, lives a
 * minute, redeems once, and redeeming it rotates the cart's key.
 */
export const CLAIM_PARAM = "claim";

/** Shape a handle must have to be believed. */
const HANDLE_RE = /^[A-Za-z0-9_-]{16,64}$/;

/** Shape of a handoff token: ``<cart id>.<secret>`` (``routers/guest_handoff.py``). */
const HANDOFF_TOKEN_RE = /^\d{1,12}\.[A-Za-z0-9_-]{16,128}$/;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private mode / storage blocked by the host's cookie settings.
    // The cart then lives only as long as the page does, which is
    // worse than persisting but better than throwing.
    return null;
  }
}

/**
 * How long a handle stays usable.
 *
 * The same thirty days the cookie transport gets
 * (``routers/quotes.py::_ensure_session_cookie``). This is the same
 * identity on the same cart, only carried differently, so the number
 * is taken from there rather than chosen: two transports of one thing
 * expiring on different days would be a difference nobody could
 * explain, and the shorter one would be the real lifetime anyway.
 *
 * Counted from the write, not slid forward on use — for the same
 * reason. The cookie is not refreshed either: ``_ensure_session_cookie``
 * returns the handle it finds and sets the header only when it mints a
 * new one. The two writes there are here as well: minting one, and
 * adopting one handed over from the frame.
 *
 * Note the row itself outlives both. Nothing prunes ``anonymous_carts``
 * today, so this bounds what the *browser* keeps announcing, not how
 * long the cart exists.
 *
 * **Why there is an expiry at all (KAN-511).** ``localStorage`` has
 * none. Without this the key outlived every journey it was minted for,
 * and ``X-Guest-Session`` — which outranks the cart cookie server-side
 * (``core/guest_session.py::resolve_guest_session``) — was sent on
 * every later storefront visit, for good, after a single pass through
 * somebody's embedded configurator.
 *
 * **Why the handle is NOT dropped when the order is placed**, which is
 * the shape the finding first proposed: it is the guest's only
 * credential for the order they have just placed. ``order_access.py``
 * admits a guest by matching the order against the cart this handle
 * keys, so clearing it at checkout would take away the "pay by card"
 * button, the return from Stripe with ``?paid=1`` and the order page
 * itself — a far worse fault than the one being fixed. The handover
 * marker cleared there (``surface.ts``) is a counter's hint and owns
 * nothing; this is a capability.
 */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** What the key holds: the handle, and when it was written. */
interface StoredHandle {
  /** The handle itself. */
  h: string;
  /** ``Date.now()`` at the moment of writing. */
  t: number;
}

/**
 * Set ONLY when persisting failed.
 *
 * It must not shadow a readable-but-empty store: a visitor who clears
 * site data expects a new cart, and serving the old handle from memory
 * until the next reload would quietly ignore them.
 */
let memoryFallback: StoredHandle | null = null;

/** Parse the stamped form. Anything else is not a handle we wrote. */
function decode(raw: string | null): StoredHandle | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { h, t } = parsed as { h?: unknown; t?: unknown };
  if (typeof h !== "string" || !HANDLE_RE.test(h)) return null;
  if (typeof t !== "number" || !Number.isFinite(t)) return null;
  return { h, t };
}

/** Drop the handle everywhere it is held. */
function forget(): void {
  memoryFallback = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // A store that refuses removal refused the write too, so there is
    // nothing there to remove.
  }
}

function read(): string | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(STORAGE_KEY) ?? null;
  } catch {
    raw = null;
  }

  // Before KAN-511 the key held the bare handle. Restamp it in place
  // rather than merely adopting it in memory: an unwritten migration
  // would restamp on every read, so the very browsers this change
  // exists for would keep their handle for ever. The clock starts now
  // — the alternative is expiring every cart in flight on the deploy
  // that ships this.
  if (raw !== null && HANDLE_RE.test(raw)) {
    write(raw);
    return raw;
  }

  const record = decode(raw) ?? memoryFallback;
  if (!record) return null;
  // A stamp in the future is a clock that moved, not a reason to throw
  // away a cart, so only age past the ceiling counts.
  if (Date.now() - record.t > MAX_AGE_MS) {
    // Dropped, not merely ignored. Ignoring it would leave the key
    // sitting there for the next reader to send again — and it is the
    // key's mere presence that moves this browser off its own cart
    // cookie, which is the whole complaint.
    forget();
    return null;
  }
  return record.h;
}

function write(handle: string): void {
  const record: StoredHandle = { h: handle, t: Date.now() };
  const store = storage();
  if (!store) {
    memoryFallback = record;
    return;
  }
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(record));
    memoryFallback = null;
  } catch {
    // Quota, or a store the host's cookie settings blocked. The
    // in-memory copy carries the handle for this page's lifetime.
    memoryFallback = record;
  }
}

function mint(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  // base64url, matching the server's ``token_urlsafe`` alphabet so a
  // handle is indistinguishable whichever side made it.
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Return the handle for this browser, minting one on first use.
 *
 * Call only where the cookie cannot be relied on (the embed). On the
 * storefront, minting a handle would quietly move every visitor off
 * the cookie transport — a behaviour change nobody asked for.
 */
export function ensureGuestHandle(): string {
  const existing = read();
  if (existing) return existing;
  const handle = mint();
  write(handle);
  return handle;
}

/** The handle to send, or null when this browser has none. */
export function currentGuestHandle(): string | null {
  return read();
}

/**
 * Redeem a handoff token from the URL fragment, then scrub it from the bar.
 *
 * The breakout from the embed carries ``#claim=<token>``. Whatever sits
 * there is scrubbed first — token or not, it has no business in
 * history or in a ``Referer`` — and only a well-formed token is sent to
 * ``POST /guest-session/redeem``, which answers with a *fresh* handle.
 * Resolves true when a handle was adopted.
 *
 * A bare handle in the fragment (the pre-KAN-1258 shape, or a crafted
 * link) is never adopted: that is exactly the fixation vector this
 * exists to close. A stale embed bundle still minting it loses that one
 * breakout's cart rather than handing every visitor somebody else's.
 * The ``?claim=`` query fallback (pre-KAN-506 bundles) is gone for the
 * same reason.
 */
export async function adoptClaimFromUrl(): Promise<boolean> {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
  const claimed = fragment.get(CLAIM_PARAM);
  if (claimed === null) return false;

  fragment.delete(CLAIM_PARAM);
  const rest = fragment.toString();
  url.hash = rest ? `#${rest}` : "";
  window.history.replaceState(
    window.history.state,
    "",
    url.pathname + url.search + url.hash,
  );

  if (!HANDOFF_TOKEN_RE.test(claimed)) return false;

  let handle: string;
  try {
    ({ handle } = await apiFetch<{ handle: string }>("/api/v1/guest-session/redeem", {
      method: "POST",
      body: { token: claimed },
    }));
  } catch {
    // Expired, already used, or forged — the visitor starts a fresh cart
    // on this side; nothing of the old one is exposed.
    return false;
  }
  if (!HANDLE_RE.test(handle)) return false;

  write(handle);
  // This browser's cart came out of an embed. The order it ends in is
  // that embed's last funnel step, and by the time it is placed there
  // is nothing else left to say so — the frame is gone.
  markEmbedHandover();
  return true;
}
